/**
 * @lodex-pro/web-admin/features/ops/hooks — #56 PR 1b (DEC-362): the ops console reads.
 *
 *   GET /v1/platform/ops/health   (PR 1a, #832)
 *   GET /v1/platform/ops/events
 *
 * Both poll every 15 s while the tab is visible and pause when it is hidden (D-1). The API
 * caches each answer for 15 s, so several admins polling cost one set of queries.
 */
import { useApiQuery, useApiOpts } from '@lodex-pro/web-shared';

/** Polling for every ops panel: 15 s, paused in a background tab. */
export const OPS_POLL = {
  refetchInterval: 15_000,
  refetchIntervalInBackground: false,
  staleTime: 10_000,
} as const;

export type OpsHealthStatus = 'HEALTHY' | 'WARNING' | 'CRITICAL';
export type OpsSeverity = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
export type OpsCategory =
  | 'API'
  | 'SYNC'
  | 'EMAIL'
  | 'PUSH'
  | 'PDF'
  | 'IMPORT'
  | 'CATALOG_SYNC'
  | 'JOB_PIPELINE'
  | 'SCHEDULER'
  | 'AUTH';

export interface SeverityCounts {
  readonly WARNING: number;
  readonly ERROR: number;
  readonly CRITICAL: number;
}

/** #56 PR 3a — the CloudWatch numbers. Absent from an API older than PR 3a. */
export type OpsCloudStatus = 'OK' | 'NOT_CONFIGURED' | 'UNAVAILABLE';

export interface OpsCloudWindow {
  readonly requests: number;
  readonly errors: number;
  readonly errorPercent: number;
}

export interface OpsCloud {
  readonly status: OpsCloudStatus;
  readonly api: {
    readonly last5m: OpsCloudWindow;
    readonly last1h: OpsCloudWindow;
    readonly p95Ms5m: number | null;
    readonly worstP95Ms1h: number | null;
    readonly healthyHosts: number | null;
    readonly unhealthyHosts: number | null;
  } | null;
  readonly rendererErrorsLast1h: number | null;
  readonly sesBounceRatePercent: number | null;
}

export interface OpsHealth {
  readonly status: OpsHealthStatus;
  readonly reasons: readonly string[];
  readonly generatedAt: string;
  readonly events: {
    readonly last15m: SeverityCounts;
    readonly last1h: SeverityCounts;
    readonly last24h: SeverityCounts;
    readonly last1hByCategory: ReadonlyArray<SeverityCounts & { readonly category: OpsCategory }>;
    readonly topProblemsLast1h: ReadonlyArray<{
      readonly category: OpsCategory;
      readonly severity: OpsSeverity;
      readonly event: string;
      readonly code: string | null;
      readonly occurrences: number;
      readonly lastOccurredAt: string | null;
    }>;
  };
  readonly queues: ReadonlyArray<{
    readonly queue: string;
    readonly label: string;
    readonly depth: number;
    readonly oldestAgeSeconds: number | null;
    readonly staleAfterSeconds: number | null;
    readonly stale: boolean;
  }>;
  readonly schedulers: ReadonlyArray<{
    readonly job: string;
    readonly label: string;
    readonly lastRunAt: string | null;
    readonly lastStatus: string | null;
    readonly stale: boolean;
    readonly failed: boolean;
  }>;
  readonly cloud?: OpsCloud;
}

// ── #56 PR 3b — the raw log lookup ──────────────────────────────────────────────

export const LOG_QUERIES_PATH = '/v1/platform/ops/logs/queries';
export type LogTemplate = 'requestId' | 'refCode' | 'errorId' | 'tenant';

export interface LogQueryStart {
  readonly id: string;
  readonly template: LogTemplate;
  readonly value: string;
  readonly window: { readonly from: string; readonly to: string };
}

export interface LogQueryResults {
  readonly status:
    | 'Scheduled'
    | 'Running'
    | 'Complete'
    | 'Failed'
    | 'Cancelled'
    | 'Timeout'
    | 'Unknown';
  /** The server's fixed field list only, e.g. '@timestamp', 'level', 'msg', 'req.url'. */
  readonly rows: ReadonlyArray<Readonly<Record<string, string>>>;
  readonly recordsMatched: number | null;
  readonly bytesScanned: number | null;
}

export interface OpsEvent {
  readonly id: string;
  readonly occurredAt: string;
  readonly lastOccurredAt: string;
  readonly occurrences: number;
  readonly env: string | null;
  readonly severity: OpsSeverity;
  readonly category: OpsCategory;
  readonly event: string;
  readonly code: string | null;
  readonly tenantId: string | null;
  readonly tenantName: string | null;
  readonly userId: string | null;
  readonly role: string | null;
  readonly route: string | null;
  readonly statusCode: number | null;
  readonly requestId: string | null;
  readonly errorId: string | null;
  readonly refCode: string | null;
  readonly entityType: string | null;
  readonly entityId: string | null;
  readonly message: string | null;
}

export interface OpsEventsPage {
  readonly window: { readonly from: string; readonly to: string };
  readonly events: readonly OpsEvent[];
  readonly nextCursor: string | null;
}

export interface OpsEventsFilter {
  readonly refCode?: string;
  readonly errorId?: string;
  readonly requestId?: string;
  readonly tenantId?: string;
  readonly category?: OpsCategory;
  readonly severity?: OpsSeverity;
  /** ISO start of the window; omitted = the API default (24 h, or a year for an exact id). */
  readonly from?: string;
  readonly before?: string;
}

/** The query string for a filter, keys in a fixed order (stable query keys and API cache). */
export function opsEventsPath(f: OpsEventsFilter): string {
  const params = new URLSearchParams();
  for (const key of [
    'refCode',
    'errorId',
    'requestId',
    'tenantId',
    'category',
    'severity',
    'from',
    'before',
  ] as const) {
    const v = f[key];
    if (v !== undefined && v !== '') params.set(key, v);
  }
  params.set('limit', '50');
  return `/v1/platform/ops/events?${params.toString()}`;
}

export function useOpsHealth(): ReturnType<typeof useApiQuery<OpsHealth>> {
  const opts = useApiOpts();
  return useApiQuery<OpsHealth>(opts, '/v1/platform/ops/health', {
    queryKey: ['platform', 'ops', 'health'],
    ...OPS_POLL,
  });
}

export function useOpsEvents(f: OpsEventsFilter): ReturnType<typeof useApiQuery<OpsEventsPage>> {
  const opts = useApiOpts();
  const path = opsEventsPath(f);
  return useApiQuery<OpsEventsPage>(opts, path, {
    queryKey: ['platform', 'ops', 'events', path],
    // An older page does not change: only the newest page polls.
    ...(f.before === undefined ? OPS_POLL : { staleTime: 60_000 }),
  });
}
